import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const TrainerSidebar = ({ isCollapsed = false, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSession, setActiveSession] = useState(3); // Mock active sessions

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/trainer-dashboard',
      icon: 'LayoutDashboard',
      description: 'Overview and monitoring'
    },
    {
      label: 'Client Management',
      path: '/client-management',
      icon: 'Users',
      description: 'Manage client profiles',
      badge: '12'
    },
    {
      label: 'Exercise Database',
      path: '/exercise-database',
      icon: 'Database',
      description: 'Exercise library'
    },
    {
      label: 'Training Plans',
      path: '/training-plan-builder',
      icon: 'FileText',
      description: 'Create and edit plans'
    }
  ];

  const quickActions = [
    {
      label: 'New Client',
      icon: 'UserPlus',
      action: () => navigate('/client-management?action=new')
    },
    {
      label: 'Emergency Stop',
      icon: 'AlertTriangle',
      variant: 'destructive',
      action: () => console.log('Emergency stop triggered')
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActive = (path) => location?.pathname === path;

  return (
    <aside 
      className={`
        fixed left-0 top-0 z-50 h-full bg-surface border-r border-border
        transition-all duration-300 ease-out
        ${isCollapsed ? 'w-20' : 'w-80'}
        lg:${isCollapsed ? 'w-20' : 'w-80'}
        md:${isCollapsed ? 'w-16' : 'w-72'}
        sm:${isCollapsed ? 'w-16' : 'w-64'}
      `}
      role="navigation"
      aria-label="Trainer navigation"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          {/* Logo always visible */}
          <div className={`${isCollapsed ? "w-10 h-10" : "w-20 h-20"} rounded-lg flex items-center justify-center`}>
            <img 
              src="./assets/images/logo.jpg" 
              alt="Logo" 
              className="object-contain w-full h-full rounded-lg" 
            />
          </div>

          {/* Text only when expanded */}
          {!isCollapsed && (
            <div>
              <h1 className="text-lg font-semibold text-foreground">FitTrainer Pro</h1>
              <p className="text-sm text-muted-foreground">Trainer Portal</p>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="breathing-focus"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
        </Button>
      </div>

      {/* Active Sessions Monitor */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div>
              <p className="text-sm font-medium text-foreground">Active Sessions</p>
              <p className="text-xs text-muted-foreground">Real-time monitoring</p>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full animate-gentle-pulse"></div>
            <span className={`font-semibold text-success ${isCollapsed ? 'text-xs' : 'text-sm'}`}>
              {activeSession}
            </span>
          </div>
        </div>
        
        {!isCollapsed && activeSession > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-3 text-xs"
            onClick={() => navigate('/trainer-dashboard')}
          >
            <Icon name="Eye" size={16} className="mr-2" />
            Monitor All Sessions
          </Button>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-2">
        <div className="space-y-1">
          {navigationItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`
                w-full flex items-center space-x-3 px-4 py-3 rounded-lg
                transition-all duration-200 ease-out text-left
                breathing-focus haptic-button
                ${isActive(item?.path)
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                }
                ${isCollapsed ? 'justify-center' : ''}
              `}
              aria-current={isActive(item?.path) ? 'page' : undefined}
              title={isCollapsed ? item?.label : undefined}
            >
              <Icon 
                name={item?.icon} 
                size={20} 
                className={isActive(item?.path) ? 'text-primary-foreground' : 'text-muted-foreground'}
              />
              
              {!isCollapsed && (
                <>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item?.label}</p>
                    <p className="text-xs opacity-75">{item?.description}</p>
                  </div>
                  
                  {item?.badge && (
                    <span className="bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded-full">
                      {item?.badge}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="pt-6 border-t border-border">
          {!isCollapsed && (
            <p className="text-xs font-medium text-muted-foreground mb-3 px-2">
              Quick Actions
            </p>
          )}
          
          <div className="space-y-2">
            {quickActions?.map((action, index) => (
              <Button
                key={index}
                variant={action?.variant || 'outline'}
                size={isCollapsed ? 'icon' : 'sm'}
                className={`
                  ${isCollapsed ? 'w-full' : 'w-full justify-start'}
                  breathing-focus haptic-button
                `}
                onClick={action?.action}
                title={isCollapsed ? action?.label : undefined}
              >
                <Icon name={action?.icon} size={16} />
                {!isCollapsed && <span className="ml-2">{action?.label}</span>}
              </Button>
            ))}
          </div>
        </div>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-border">
        {!isCollapsed ? (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
              <Icon name="User" size={20} className="text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Dr. Sarah Johnson</p>
              <p className="text-xs text-muted-foreground">Senior Trainer</p>
            </div>
            <Button variant="ghost" size="icon" className="breathing-focus">
              <Icon name="Settings" size={16} />
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="icon" className="w-full breathing-focus">
            <Icon name="User" size={20} />
          </Button>
        )}
      </div>
    </aside>
  );
};

export default TrainerSidebar;
